import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, BellOff, CalendarCheck, CheckCheck, CreditCard, LifeBuoy, MapPin, Megaphone, MessageSquare } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { PageLoading, EmptyState, ErrorState } from "../components/States";
import { api } from "../lib/api";
import { fmtDateTime, setPageTitle } from "../lib/format";

// Category model (Part 17): derived purely from the server `kind` — no new
// backend fields, no fake grouping. Color is never the only signal: every row
// shows an icon + text and the unread state adds a label-level dot + border.
const CATEGORIES = {
  bookings: { label: "Bookings", icon: CalendarCheck },
  payments: { label: "Payments", icon: CreditCard },
  support: { label: "Support", icon: LifeBuoy },
  messages: { label: "Messages", icon: MessageSquare },
  announcements: { label: "Announcements", icon: Megaphone },
  listings: { label: "Listings", icon: MapPin },
  other: { label: "Other", icon: Bell },
};

function categoryOf(kind = "") {
  if (kind.startsWith("booking") || kind.startsWith("otp") || kind === "hold_expired"
      || kind === "hold_expiring_soon"
      || kind === "parking" || kind === "extension" || kind === "vehicle_mismatch") return "bookings";
  if (kind.startsWith("payment") || kind === "capture_failed" || kind.startsWith("wallet")
      || kind.startsWith("instant") || kind === "service_fee" || kind === "tax" || kind === "discount") return "payments";
  if (kind.startsWith("support") || kind.startsWith("issue")) return "support";
  if (kind === "new_message") return "messages";
  if (kind === "broadcast") return "announcements";
  if (kind.startsWith("listing")) return "listings";
  return "other";
}

export default function Notifications() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setError(null);
    api.notifications().then(setItems).catch((e) => { setError(e.message); setItems([]); });
  };
  useEffect(() => { setPageTitle("Notifications"); load(); }, []);

  const markAll = async () => {
    try { await api.markAllRead(); load(); } catch { /* noop */ }
  };

  const openItem = async (n) => {
    if (!n.read) { try { await api.markRead(n.notification_id); } catch { /* noop */ } }
  };

  const withCategory = useMemo(
    () => (items || []).map((n) => ({ ...n, _cat: categoryOf(n.kind) })),
    [items]
  );
  const presentCats = useMemo(
    () => Object.keys(CATEGORIES).filter((c) => withCategory.some((n) => n._cat === c)),
    [withCategory]
  );
  const visible = withCategory.filter((n) =>
    filter === "all" ? true : filter === "unread" ? !n.read : n._cat === filter);

  return (
    <div className="min-h-screen flex flex-col" data-testid="notifications-page">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <div className="flex items-center justify-between">
          <div>
            <p className="section-label">Updates</p>
            <h1 className="type-display mt-3">Notifications</h1>
          </div>
          {items?.some((n) => !n.read) && (
            <button onClick={markAll} data-testid="mark-all-read-btn" className="btn-secondary !px-4 !py-2 text-xs">
              <CheckCheck className="w-4 h-4" /> Mark all read
            </button>
          )}
        </div>

        {withCategory.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Notification filters" data-testid="notification-filters">
            {[{ id: "all", label: "All" }, { id: "unread", label: "Unread" },
              ...presentCats.map((c) => ({ id: c, label: CATEGORIES[c].label }))].map((f) => (
              <button key={f.id} role="tab" aria-selected={filter === f.id}
                      onClick={() => setFilter(f.id)}
                      data-testid={`notif-filter-${f.id}`}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        filter === f.id
                          ? "bg-[#E50914] border-[#E50914] text-white"
                          : "bg-[#1F2328] border-[#27272A] text-zinc-400 hover:border-[#3F3F46] hover:text-zinc-200"}`}>
                {f.label}
              </button>
            ))}
          </div>
        )}

        <div className="mt-8">
          {error ? <ErrorState body={error} onRetry={load} /> : items === null ? <PageLoading label="Loading notifications…" /> :
            withCategory.length === 0 ? (
              <EmptyState icon={<BellOff className="w-7 h-7 text-[#FF3B45]" />} title="No notifications yet"
                body="Booking updates, payment receipts, and host messages will appear here." />
            ) : visible.length === 0 ? (
              <EmptyState icon={<Bell className="w-7 h-7 text-zinc-600" />} title="Nothing here"
                body="No notifications match this filter." />
            ) : (
              <div className="space-y-2.5" data-testid="notifications-list">
                {visible.map((n) => {
                  const Cat = CATEGORIES[n._cat];
                  const Icon = Cat.icon;
                  const inner = (
                    <div className={`p-4 flex gap-3.5 rounded-2xl border transition-all duration-200 bg-[#0D1114]/70 backdrop-blur-md ${
                      n.read
                        ? "border-[#1F2328] opacity-60"
                        : "border-[#27272A] border-l-2 border-l-[#E50914] shadow-[0_0_24px_rgba(229,9,20,0.10)] hover:border-[#E50914]/40"
                    }`}>
                      <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                        n.read ? "bg-[#1F2328] text-zinc-500" : "bg-[#E50914]/12 text-[#FF3B45]"}`} aria-hidden="true">
                        <Icon className="w-4 h-4" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold truncate">{n.title}</p>
                          <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-zinc-600 shrink-0">{Cat.label}</span>
                        </div>
                        <p className="text-sm text-zinc-400 mt-0.5">{n.body}</p>
                        <p className="text-[11px] text-zinc-600 mt-1.5">{fmtDateTime(n.created_at)}</p>
                      </div>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-[#E50914] mt-1.5 shrink-0" aria-label="Unread" />}
                    </div>
                  );
                  return n.booking_id ? (
                    <Link key={n.notification_id} to={`/bookings/${n.booking_id}`} onClick={() => openItem(n)} data-testid={`notification-${n.notification_id}`}>{inner}</Link>
                  ) : (
                    <button key={n.notification_id} onClick={() => openItem(n)} data-testid={`notification-${n.notification_id}`} className="w-full text-left">{inner}</button>
                  );
                })}
              </div>
            )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
